import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import StatsOverview from "../components/StatsOverview";
import ReviewerDashboard from "../components/ReviewerDashboard";
import AccountManagerDashboard from "../components/AccountManagerDashboard";
import ManualEntryModal from "../components/ManualEntryModal";
import FileTable from "../components/FileTable";
import UserManagement from "../components/UserManagement";
import ClientManagement from "../components/ClientManagement";
import DailyReports from "../components/DailyReports";
import { INITIAL_STATE } from "../utils/constants";
import * as XLSX from "xlsx";
import {
    calculateTAT,
    calculateSLA,
    calculateShift,
    getDayName,
    calculateValueTenPercent,
    getStatusFromSubstatus,
    parseCustomDate,
    formatDateTime
} from "../utils/helpers";

const AdminDashboard = () => {
    const getTodayDateString = () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, "0");
        const day = String(now.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const todayStr = getTodayDateString();

    const [dateFilterType, setDateFilterType] = useState("Today");
    const [startDate, setStartDate] = useState(todayStr);
    const [endDate, setEndDate] = useState(todayStr);
    const [selectedClient, setSelectedClient] = useState("All");

    // Master Global Dropdown Filter Switcher (Defaults strictly to All/Total)
    const [shiftFilter, setShiftFilter] = useState("All");

    const [currentUser, setCurrentUser] = useState({ name: "Waiting...", role: "None" });
    const [activeDept, setActiveDept] = useState("Dashboard");
    const [activeClient, setActiveClient] = useState("");
    const [files, setFiles] = useState([]);

    // Dedicated clean array to store raw, unfiltered backend records for the sidebar notification dots
    const [rawBackgroundFiles, setRawBackgroundFiles] = useState([]);

    const [aggregatedSummary, setAggregatedSummary] = useState(null);
    const [showManualModal, setShowManualModal] = useState(false);
    const [formData, setFormData] = useState(INITIAL_STATE);
    const [openMenus, setOpenMenus] = useState({ QC: false, Revised: false });
    const [loading, setLoading] = useState(false);

    const [clientsList, setClientsList] = useState([]);
    const [clientTatMap, setClientTatMap] = useState({});

    const [userLocations, setUserLocations] = useState({});
    const [selectedLocation, setSelectedLocation] = useState("All");
    const [exportType, setExportType] = useState("All");

    const fetchUserLocations = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return;
            const res = await axios.get("/api/users/locations", {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUserLocations(res.data || {});
        } catch (err) {
            console.error("Failed to fetch user locations mapping", err);
        }
    };

    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showNotificationPanel, setShowNotificationPanel] = useState(false);
    const [activeToast, setActiveToast] = useState(null);
    const dashboardToastTimeoutRef = useRef(null);

    const showDashboardToast = (message, title = "Success", type = "success") => {
        if (dashboardToastTimeoutRef.current) {
            clearTimeout(dashboardToastTimeoutRef.current);
        }
        setActiveToast({ message, title, type });
        dashboardToastTimeoutRef.current = setTimeout(() => {
            setActiveToast(null);
        }, 4000);
    };

    const fetchNotifications = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return;
            const showAll = currentUser.role === "Admin" || currentUser.role === "Manager" || currentUser.role === "AccountManager";
            const res = await axios.get("/api/notifications", {
                params: showAll ? { all: "true" } : {},
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data && Array.isArray(res.data)) {
                const newNotifications = res.data;
                const newUnreadCount = newNotifications.filter(n => !n.is_read).length;
                let hasNewUnread = false;

                const isManagerUser = currentUser.role === "Admin" || currentUser.role === "Manager" || currentUser.role === "AccountManager";

                if (notifications.length > 0) {
                    const newUnread = newNotifications.filter(n => {
                        const isNewId = !notifications.some(existing => existing.id === n.id);
                        if (isManagerUser) {
                            return isNewId;
                        } else {
                            return isNewId && !n.is_read;
                        }
                    });
                    if (newUnread.length > 0 && !isManagerUser) {
                        hasNewUnread = true;
                        setActiveToast(newUnread[0]);
                        setTimeout(() => setActiveToast(null), 6000);

                        if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
                            try {
                                const nativeNotify = new Notification("File Assigned! 🔔", {
                                    body: newUnread[0].message,
                                    tag: `assignment-${newUnread[0].id}`
                                });
                                nativeNotify.onclick = () => {
                                    window.focus();
                                    setShowNotificationPanel(true);
                                };
                            } catch (e) {
                                console.error("Desktop notification failed to show:", e);
                            }
                        }
                    }
                } else {
                    const recentUnread = newNotifications.filter(n => {
                        const isVeryRecent = (new Date() - new Date(n.created_at)) < 15000;
                        if (isManagerUser) {
                            return isVeryRecent;
                        } else {
                            return isVeryRecent && !n.is_read;
                        }
                    });
                    if (recentUnread.length > 0 && !isManagerUser) {
                        hasNewUnread = true;
                        setActiveToast(recentUnread[0]);
                        setTimeout(() => setActiveToast(null), 6000);

                        if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
                            try {
                                const nativeNotify = new Notification("File Assigned! 🔔", {
                                    body: recentUnread[0].message,
                                    tag: `assignment-${recentUnread[0].id}`
                                });
                                nativeNotify.onclick = () => {
                                    window.focus();
                                    setShowNotificationPanel(true);
                                };
                            } catch (e) {
                                console.error("Desktop notification failed to show:", e);
                            }
                        }
                    }
                }

                setNotifications(newNotifications);
                setUnreadCount(isManagerUser ? 0 : newUnreadCount);

                if (hasNewUnread) {
                    fetchData();
                }
            }
        } catch (err) {
            console.error("Failed to fetch notifications:", err);
        }
    };

    const markAllNotificationsRead = async () => {
        try {
            const token = localStorage.getItem("token");
            await axios.put("/api/notifications/mark-read", {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchNotifications();
            fetchData();
        } catch (err) {
            console.error("Failed to mark notifications as read:", err);
        }
    };

    const markSingleNotificationRead = async (id) => {
        try {
            const token = localStorage.getItem("token");
            await axios.put(`/api/notifications/${id}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchNotifications();
            fetchData();
        } catch (err) {
            console.error("Failed to mark single notification as read:", err);
        }
    };

    useEffect(() => {
        if (typeof window !== "undefined" && "Notification" in window) {
            if (Notification.permission === "default") {
                Notification.requestPermission();
            }
        }
    }, []);

    const fetchNotificationsRef = useRef(fetchNotifications);
    useEffect(() => {
        fetchNotificationsRef.current = fetchNotifications;
    });

    useEffect(() => {
        if (currentUser && currentUser.name && currentUser.name !== "Waiting...") {
            fetchNotificationsRef.current();
            const interval = setInterval(() => {
                fetchNotificationsRef.current();
            }, 10000);
            return () => clearInterval(interval);
        }
    }, [currentUser]);

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return;
            const res = await axios.get("/api/clients", {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data && Array.isArray(res.data)) {
                setClientsList(res.data);
                const tatMap = {};
                res.data.forEach(client => {
                    tatMap[client.name.toLowerCase().trim()] = {
                        normal_qc: parseFloat(client.normal_qc),
                        priority_qc: parseFloat(client.priority_qc),
                        normal_revised: parseFloat(client.normal_revised),
                        priority_revised: parseFloat(client.priority_revised)
                    };
                });
                setClientTatMap(tatMap);
            }
        } catch (err) {
            console.error("Failed to fetch clients in AdminDashboard", err);
        }
    };

    const isAdmin = currentUser.role === "Admin" || currentUser.role === "Manager";
    const isAccountManager = currentUser.role === "AccountManager";
    const isManager = isAdmin || isAccountManager;

    const btnStyle = {
        padding: "6px 12px",
        border: "none",
        borderRadius: "6px",
        cursor: "pointer",
        fontSize: "12px",
        fontWeight: "600",
        backgroundColor: "#f1f5f9",
        color: "#64748b"
    };

    const activeBtnStyle = {
        ...btnStyle,
        backgroundColor: "#00a3e0",
        color: "white"
    };

    const getFilterButtonStyle = (type) => {
        return dateFilterType === type ? activeBtnStyle : btnStyle;
    };

    const dateInputStyle = {
        padding: "5px 10px",
        borderRadius: "6px",
        border: "1px solid #cbd5e1",
        fontSize: "13px",
        outline: "none",
        color: "#334155"
    };

    const normalizeText = (value) => {
        if (value === null || value === undefined) return "";
        return String(value).trim().toLowerCase();
    };

    const isClosedOrExceptionFile = (file) => {
        const status = normalizeText(file.status);
        const substatus = normalizeText(file.substatus);

        if (!status && !substatus) return false;

        return (
            status.includes("completed") ||
            status.includes("rejected") ||
            status.includes("do not review") ||
            status.includes("wrong report") ||
            status.includes("complete by client") ||
            substatus.includes("completed") ||
            substatus.includes("rejected") ||
            substatus.includes("do not review") ||
            substatus.includes("wrong report") ||
            substatus.includes("complete by client")
        );
    };

    const normalizeClientName = (value) => {
        const client = String(value || "").trim();
        if (client.toLowerCase() === "anm") return "MMA";
        return client;
    };

    const parseLocalDate = (value) => {
        if (!value) return null;
        if (value instanceof Date && !isNaN(value.getTime())) {
            return value;
        }

        const str = String(value).trim().split(" ")[0].replace(/\//g, "-");
        const dateParts = str.split("-").map(Number);
        if (dateParts.length === 3) {
            let year, month, day;
            if (dateParts[0] > 1000) {
                [year, month, day] = dateParts;
            } else if (dateParts[2] > 1000) {
                if (dateParts[0] > 12) {
                    [day, month, year] = dateParts;
                } else {
                    [month, day, year] = dateParts;
                }
            }
            if (year && month && day && !isNaN(year) && !isNaN(month) && !isNaN(day)) {
                return new Date(year, month - 1, day);
            }
        }

        const d = new Date(str);
        if (isNaN(d.getTime())) return null;
        return d;
    };

    const dashboardFiles = useMemo(() => {
        let filtered = files;

        if (!isManager) {
            if (!currentUser || currentUser.name === "Waiting...") return [];

            filtered = filtered.filter(f =>
                normalizeText(f.assigned_to) === normalizeText(currentUser.name)
            );
        }

        if (selectedClient !== "All" && selectedClient !== "") {
            filtered = filtered.filter(file => {
                let targetName = selectedClient;
                if (!isNaN(selectedClient) && clientsList && clientsList.length > 0) {
                    const matchClient = clientsList.find(c => String(c.id) === String(selectedClient));
                    if (matchClient) targetName = matchClient.name;
                }
                const fClient = file.client_name || file.activeClient || file.client_clean || "";
                return normalizeText(fClient) === normalizeText(targetName);
            });
        }

        if ((activeDept === "Dashboard" || activeDept === "Hourly Monitor" || activeDept === "Report") && dateFilterType !== "All") {
            filtered = filtered.filter(file => {
                if (!file.date_column) return false;

                const startObj = parseLocalDate(startDate);
                const endObj = parseLocalDate(endDate);
                const fileDateObj = parseLocalDate(file.date_column);

                if (!fileDateObj || !startObj || !endObj) return false;

                const startTime = new Date(startObj).setHours(0, 0, 0, 0);
                const endTime = new Date(endObj).setHours(0, 0, 0, 0);
                const fileTime = new Date(fileDateObj).setHours(0, 0, 0, 0);

                return fileTime >= startTime && fileTime <= endTime;
            });
        }

        return filtered;
    }, [files, isManager, currentUser, activeDept, dateFilterType, startDate, endDate, selectedClient]);

    // Utility to convert Excel serial dates/datetimes to ISO strings
    const convertExcelDate = (val) => {
        if (!val) return null;
        if (typeof val === 'number') {
            const dateObj = XLSX.SSF.parse_date_code(val);
            const d = new Date(dateObj.y, dateObj.m - 1, dateObj.d, dateObj.H, dateObj.M, dateObj.S);
            if (!isNaN(d.getTime())) {
                if (dateObj.H || dateObj.M || dateObj.S) {
                    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:00`;
                }
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            }
        }
        return String(val).trim();
    };

    const handleBulkImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const workbook = XLSX.read(bstr, { type: "binary", cellDates: false });
                const wsname = workbook.SheetNames[0];
                const ws = workbook.Sheets[wsname];
                const rawData = XLSX.utils.sheet_to_json(ws);

                const processedData = rawData.map(row => {
                    const findKey = (arr) => {
                        const found = Object.keys(row).find(k => {
                            const cleanKey = k.trim().toLowerCase().replace(/[\s\._-]/g, '');
                            return arr.includes(cleanKey);
                        });
                        return found ? row[found] : null;
                    };

                    let inTime = convertExcelDate(findKey(['intime', 'intimecolumn']));
                    let assignTime = convertExcelDate(findKey(['assigntime', 'assigntimecolumn', 'assignedtime', 'assigndate']));
                    let outTime = convertExcelDate(findKey(['outtime', 'outtimecolumn']));
                    let dateCol = convertExcelDate(findKey(['datecolumn', 'date']));
                    const loanNo = findKey(['loannumber', 'loan_number', 'loanno', 'filenumber', 'file_number']);

                    let computedShift = null;

                    if (inTime && inTime !== "-") {
                        computedShift = calculateShift(inTime);
                    }

                    let calculatedTat = (inTime && outTime && inTime !== "-" && outTime !== "-") ? calculateTAT(inTime, outTime) : null;

                    return {
                        ...row,
                        date_column: dateCol,
                        loan_number: loanNo,
                        in_time: inTime,
                        assign_time: assignTime,
                        out_time: outTime,
                        shift: computedShift || "-",
                        tat: calculatedTat || "-",
                        day: dateCol ? getDayName(dateCol) : "-",
                        activeClient: selectedClient !== "All" ? selectedClient : (findKey(['clientname', 'client']) || "LVS"),
                        activeDept: activeDept !== "Dashboard" ? activeDept : "QC"
                    };
                });

                const token = localStorage.getItem("token");
                await axios.post("/api/bulk-import", { data: processedData }, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                showDashboardToast("Bulk Data Records Imported and Computed Successfully!", "Import Success", "success");
                fetchData();
            } catch (err) {
                console.error("Bulk Import Frontend Parsing fault:", err);
                showDashboardToast("Failed to parse or save imported spreadsheet data matrix rows.", "Import Error", "error");
            }
        };
        reader.readAsBinaryString(file);
    };

    const handleExport = async () => {
        if (!isAdmin) {
            showDashboardToast("Permission denied. Only Admins can download reports.", "Access Denied", "error");
            return;
        }
        if (activeDept === "Report") {
            window.print();
            return;
        }

        const token = localStorage.getItem("token");
        let targetFilesToExport = [];

        setLoading(true);
        try {
            if (activeDept === "Dashboard") {
                const dbClient = selectedClient === "All" || selectedClient === "All Clients" ? "" : selectedClient;
                const dbDept = exportType === "All" ? "" : (exportType === "Revised" ? "Revised" : "QC");
                const dbShift = shiftFilter === "All" || shiftFilter === "Total" || shiftFilter === "Combined Total" ? "" : shiftFilter;
                const dbLocation = selectedLocation === "All" ? "" : selectedLocation;

                const params = {
                    department: dbDept,
                    client: dbClient,
                    shiftFilter: dbShift,
                    location: dbLocation,
                    employee: !isManager && currentUser.name !== "Waiting..." ? currentUser.name : ""
                };

                if (dateFilterType !== "All") {
                    params.startDate = startDate;
                    params.endDate = endDate;
                }

                const res = await axios.get("/api/files/filter", {
                    params,
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (Array.isArray(res.data)) {
                    targetFilesToExport = res.data;
                }
            } else {
                targetFilesToExport = [...dashboardFiles];

                if (exportType && exportType !== "All") {
                    targetFilesToExport = targetFilesToExport.filter(f => {
                        const dept = String(f.department || f.activeDept || "").toLowerCase().trim();
                        if (exportType === "Revised") {
                            return dept.includes("revised") || dept.includes("report");
                        } else if (exportType === "QC") {
                            return dept.includes("qc") || (!dept.includes("revised") && !dept.includes("report"));
                        }
                        return true;
                    });
                }
            }

            if (targetFilesToExport.length === 0) {
                showDashboardToast("No tracking files available to export for the selected filters.", "Export Error", "error");
                setLoading(false);
                return;
            }

            const forbiddenColumns = [
                "_id", "id", "__v",
                "activeDept", "activeClient", "department",
                "locked_by", "last_moved_at", "is_duplicate",
                "assigned_by", "substatus",
                "pst_in", "inflow_pst", "time_in",
                "pst_out", "outflow_pst", "time_out",
                "received_date",
                "client_id", "clientId",
                "comments",
                "sla_start_time", "sla_end_time",
                "reviewer_name"
            ];
            const orderedKeys = [
                // Unhighlighted columns (Left side)
                "sr_no", "date_column", "file_number", "loan_number", "borrower_name", "address",
                "in_time", "assign_time", "out_time", "tat", "reviewer_tat",
                "client_name", "assigned_to", "location", "priority", "lender_name", "product_code",
                "day", "status", "revisions", "sla_status", "shift",
                // Yellow-highlighted columns (Moved to Right side)
                "appraiser_name", "loan_type", "lower_than_contract", "crosscheck_results",
                "pp", "market_value", "ten_percent", "notes_message",
                "qc_by", "feedback", "training", "training_findings",
                "auditor_findings", "feedback2", "production_log",
                "escalations", "doubts", "advisor_note"
            ];
            let columnsToExport = Object.keys(targetFilesToExport[0] || {})
                .filter(k => !forbiddenColumns.includes(k));

            if (!columnsToExport.includes("location")) {
                columnsToExport.push("location");
            }
            if (!columnsToExport.includes("reviewer_tat")) {
                columnsToExport.push("reviewer_tat");
            }

            columnsToExport.sort((a, b) => {
                const idxA = orderedKeys.indexOf(a);
                const idxB = orderedKeys.indexOf(b);
                if (idxA === -1 && idxB === -1) return 0;
                if (idxA === -1) return 1;
                if (idxB === -1) return -1;
                return idxA - idxB;
            });

            const exportData = targetFilesToExport.map((file, index) => {
                const row = {};

                columnsToExport.forEach(key => {
                    if (String(key).toLowerCase() === "sr_no") {
                        row["SR NO"] = index + 1;
                    } else {
                        let header = key.replace(/_/g, " ").toUpperCase();
                        if (key === "date_column") header = "DATE";
                        if (key === "pp") header = "PURCHASE PRICE";
                        if (key === "tat") header = "SLA TAT";
                        if (key === "reviewer_tat") header = "REVIEWER TAT";
                        let val = key === "reviewer_tat"
                            ? (calculateTAT(file.assign_time, file.out_time) || "")
                            : file[key];

                        if (key === "location" && (!val || val === "-")) {
                            val = userLocations[file.assigned_to] || userLocations[file.reviewer_name] || "";
                        }

                        if (val === 0 || val === "0" || val === "0.0" || val === "0.00") {
                            val = "";
                        }



                        if (key === "date_column") {
                            if (val && val !== "-") {
                                const cleanStr = String(val).trim().split("T")[0].split(" ")[0].replace(/\//g, "-");
                                if (/^\d{4}-\d{2}-\d{2}$/.test(cleanStr)) {
                                    const [y, m, d] = cleanStr.split("-");
                                    val = `${d.padStart(2, "0")}-${m.padStart(2, "0")}-${y}`;
                                } else if (/^\d{2}-\d{2}-\d{4}$/.test(cleanStr)) {
                                    val = cleanStr;
                                } else {
                                    const dObj = new Date(val);
                                    if (!isNaN(dObj.getTime())) {
                                        const day = String(dObj.getUTCDate()).padStart(2, "0");
                                        const month = String(dObj.getUTCMonth() + 1).padStart(2, "0");
                                        const year = dObj.getUTCFullYear();
                                        val = `${day}-${month}-${year}`;
                                    } else {
                                        val = String(val);
                                    }
                                }
                            } else {
                                val = "";
                            }
                        } else if (
                            key === "in_time" ||
                            key === "out_time" ||
                            (typeof val === "string" && val.includes("T") && val.includes("Z"))
                        ) {
                            if (val && val !== "-") {
                                const dObj = new Date(val);
                                if (!isNaN(dObj.getTime())) {
                                    const day = String(dObj.getDate()).padStart(2, "0");
                                    const month = String(dObj.getMonth() + 1).padStart(2, "0");
                                    const year = dObj.getFullYear();
                                    const hrs = String(dObj.getHours()).padStart(2, "0");
                                    const mins = String(dObj.getMinutes()).padStart(2, "0");

                                    val = `${day}-${month}-${year} ${hrs}:${mins}`;
                                } else {
                                    val = "";
                                }
                            }
                        }

                        row[header] = val === null || val === undefined || val === "-" ? "" : val;
                    }
                });

                return row;
            });

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Production_Report");

            const fileName = `GoSource_${selectedClient}_Files_Report_${startDate}_to_${endDate}.xlsx`;
            XLSX.writeFile(workbook, fileName);

        } catch (err) {
            console.error("Export calculation fault:", err);
            showDashboardToast("Failed to export backing records. Check terminal connection.", "Export Error", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem("token");
        const savedUserInfo = localStorage.getItem("userInfo");

        if (token && savedUserInfo) {
            setCurrentUser(JSON.parse(savedUserInfo));
            fetchClients();
            fetchUserLocations();
        } else {
            window.location.href = "/login";
        }
    }, []);

    useEffect(() => {
        const restrictedDepts = ["User Management", "Client Management", "Report"];
        if (restrictedDepts.includes(activeDept) && currentUser.name !== "Waiting..." && !isAdmin) {
            setActiveDept("Dashboard");
        }
    }, [activeDept, currentUser, isAdmin]);

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("userInfo");
        window.location.href = "/login";
    };

    const assignedClientNames = useMemo(() => {
        if (!currentUser || currentUser.name === "Waiting...") return [];

        const dataSource = !isManager ? rawBackgroundFiles : files;

        const pendingWork = dataSource.filter(file => {
            const isMyFile = normalizeText(file.assigned_to) === normalizeText(currentUser.name);
            return isMyFile && !isClosedOrExceptionFile(file);
        });

        const clientsWithWork = pendingWork
            .map(file => normalizeClientName(file.activeClient || file.client_name))
            .filter(Boolean);

        return [...new Set(clientsWithWork)];
    }, [files, rawBackgroundFiles, isManager, currentUser.name]);

    const setQuickRange = (type) => {
        const now = new Date();
        const yearStr = now.getFullYear();
        const monthStr = String(now.getMonth() + 1).padStart(2, "0");
        const dayStr = String(now.getDate()).padStart(2, "0");
        const currentDayFormattedString = `${yearStr}-${monthStr}-${dayStr}`;

        if (type === "Today") {
            setStartDate(currentDayFormattedString);
            setEndDate(currentDayFormattedString);
            setDateFilterType("Today");
        } else if (type === "Month") {
            const firstDayOfMonthString = `${yearStr}-${monthStr}-01`;
            const lastDayObj = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            const lastDayOfMonthString = `${yearStr}-${monthStr}-${String(lastDayObj.getDate()).padStart(2, "0")}`;

            setStartDate(firstDayOfMonthString);
            setEndDate(lastDayOfMonthString);
            setDateFilterType("Month");
        } else if (type === "Custom") {
            setDateFilterType("Custom");
        }
    };

    const fetchData = async (silent = false) => {
        const isSilent = silent === true;
        const token = localStorage.getItem("token");
        if (!token || activeDept === "Hourly Monitor" || activeDept === "Reviewer Dashboard") return;

        if (!isSilent) setLoading(true);
        try {
            const clientToUse = (activeDept === "Dashboard" || activeDept === "Report") ? selectedClient : activeClient;
            const dbDept = activeDept;
            const dbClient = clientToUse === "MMA" ? "ANM" : clientToUse;

            const params = {
                department: activeDept === "Report" ? "" : activeDept,
                client: activeDept === "Report" ? "All" : dbClient,
                shiftFilter: activeDept === "Report" ? "All" : shiftFilter,
                location: activeDept === "Report" ? "All" : selectedLocation
            };
            if (!isManager && currentUser.name !== "Waiting...") {
                params.employee = currentUser.name;
            }
            if (activeDept === "Dashboard" || activeDept === "Report") {
                params.startDate = startDate;
                params.endDate = endDate;
            }


            const response = await axios.get("/api/files/filter", {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            if ((activeDept === "Dashboard" || activeDept === "Report") && response.data && response.data.isBackendAggregated) {
                setAggregatedSummary(response.data);

                if (!isManager && currentUser.name !== "Waiting...") {
                    try {
                        const backgroundRowsResponse = await axios.get("/api/files/filter", {
                            params: {
                                department: "",
                                client: "All",
                                employee: currentUser.name,
                                startDate,
                                endDate,
                                includeActive: "true"
                            },
                            headers: { Authorization: `Bearer ${token}` }
                        });

                        if (Array.isArray(backgroundRowsResponse.data)) {
                            setRawBackgroundFiles(backgroundRowsResponse.data);
                            setFiles(backgroundRowsResponse.data);
                        }
                    } catch (bgErr) {
                        console.error("Background sidebar sync fault:", bgErr);
                    }
                }
            } else if (Array.isArray(response.data)) {
                setFiles(response.data);

                if (!isManager) {
                    if (rawBackgroundFiles.length === 0) {
                        setRawBackgroundFiles(response.data);
                    } else {
                        setRawBackgroundFiles(prevRaw => {
                            return prevRaw.map(oldFile => {
                                const freshMatch = response.data.find(f => f.id === oldFile.id);
                                return freshMatch ? freshMatch : oldFile;
                            });
                        });
                    }
                }
            }
        } catch (err) {
            if (err.response && (err.response.status === 403 || err.response.status === 401)) {
                handleLogout();
            }
            console.error("Data Fetch Error:", err);
        } finally {
            if (!isSilent) setLoading(false);
        }
    };

    useEffect(() => {
        if (activeDept !== "User Management" && currentUser.name !== "Waiting...") {
            fetchData(false);
            if (activeDept === "Dashboard") {
                const baseUrl = axios.defaults.baseURL || "";
                const eventSource = new EventSource(`${baseUrl}/api/events`);
                eventSource.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        if (data.type === "FILES_UPDATED") {
                            fetchData(true);
                        }
                    } catch (err) {
                        // Ignore parse errors
                    }
                };
                return () => {
                    eventSource.close();
                };
            }
        }
    }, [activeDept, activeClient, selectedClient, startDate, endDate, shiftFilter, currentUser, selectedLocation]);

    const handleManualInputChange = (e) => {
        const { name, value } = e.target;
        let updatedData = { ...formData, [name]: value };

        if (name === "assigned_to") {
            updatedData.reviewer_name = value;
        }

        if (name === "in_time" || name === "assign_time" || name === "out_time" || name === "priority" || name === "client_name" || name === "department") {
            if (name === "in_time" || name === "out_time") {
                if (name === "in_time") updatedData.shift = calculateShift(value);
            }

            updatedData.tat = calculateTAT(updatedData.in_time, updatedData.out_time);
            updatedData.reviewer_tat = calculateTAT(updatedData.assign_time, updatedData.out_time);

            updatedData.sla_status = calculateSLA(
                updatedData.tat,
                updatedData.client_name || activeClient,
                updatedData.department || activeDept,
                updatedData.priority,
                clientTatMap
            );
        }

        if (name === "date_column") updatedData.day = getDayName(value);

        if (name === "substatus") {
            const lowerVal = String(value || "").trim().toLowerCase();
            if (lowerVal === "wrong report" || lowerVal === "do not review" || lowerVal === "complete by client") {
                updatedData.status = value;
            } else {
                const mappedStatus = getStatusFromSubstatus(value);
                if (mappedStatus) updatedData.status = mappedStatus;
            }
        }

        if (name === "pp" || name === "market_value") {
            updatedData.ten_percent = calculateValueTenPercent(updatedData.pp, updatedData.market_value);
        }

        setFormData(updatedData);
    };

    const submitManualEntry = async (e) => {
        if (e) e.preventDefault();

        const dbDept = activeDept;
        const dbClient = activeClient === "MMA" ? "ANM" : activeClient;

        const fileNum = String(formData.file_number || formData.loan_number || "").trim();
        const dateCol = String(formData.date_column || "").trim();
        const clientVal = String(formData.client_name || dbClient || "").trim();
        const addressVal = String(formData.address || "").trim();
        const assignedVal = String(formData.assigned_to || (isManager ? "" : currentUser.name) || "").trim();
        const inTimeVal = String(formData.in_time || "").trim();
        const assignTimeVal = String(formData.assign_time || "").trim();
        const outTimeVal = String(formData.out_time || "").trim();

        const missing = [];
        if (!fileNum || fileNum === "-") missing.push("File Number");
        if (!dateCol || dateCol === "-") missing.push("Date");
        if (!inTimeVal || inTimeVal === "-") missing.push("In Time");
        if (!assignTimeVal || assignTimeVal === "-") missing.push("Assign Time");

        if (!isManager) {
            if (!clientVal || clientVal === "-") missing.push("Client");
            if (!addressVal || addressVal === "-") missing.push("Address");
            if (!assignedVal || assignedVal === "-") missing.push("Assigned To");
            if (!outTimeVal || outTimeVal === "-") missing.push("Out Time");
        }

        if (missing.length > 0) {
            showDashboardToast(`Cannot save! Please fill all mandatory fields: ${missing.join(", ")}`, "Required Fields Missing", "error");
            return;
        }

        try {
            const cleanInTime = formData.in_time ? (formatDateTime(parseCustomDate(formData.in_time)) || formData.in_time) : formData.in_time;
            const cleanAssignTime = formData.assign_time ? (formatDateTime(parseCustomDate(formData.assign_time)) || formData.assign_time) : formData.assign_time;
            const cleanOutTime = formData.out_time ? (formatDateTime(parseCustomDate(formData.out_time)) || formData.out_time) : formData.out_time;

            const finalEntry = {
                ...formData,
                in_time: cleanInTime,
                assign_time: cleanAssignTime,
                out_time: cleanOutTime,
                department: formData.department || dbDept,
                client_name: formData.client_name || dbClient,
                activeDept: formData.department || dbDept,
                activeClient: formData.client_name || dbClient,
                assigned_to: formData.assigned_to || (isManager ? "" : currentUser.name)
            };

            const token = localStorage.getItem("token");
            await axios.post("/api/manual-entry", finalEntry, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShowManualModal(false);
            setFormData(INITIAL_STATE);
            fetchData();
            showDashboardToast("Record saved successfully!", "Success", "success");
        } catch (err) {
            showDashboardToast("Error saving record.", "Error", "error");
        }
    };

    return (
        <div style={{ display: "flex", height: "100vh", backgroundColor: "#f4f7f9", fontFamily: "sans-serif", position: "relative" }} className="main-portal-container">

            {loading && (
                <div style={{
                    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(255, 255, 255, 0.75)", display: "flex",
                    flexDirection: "column", alignItems: "center", justifyContent: "center",
                    zIndex: 9999, backdropFilter: "blur(2px)"
                }}>
                    <div style={{
                        width: "40px", height: "40px",
                        border: "4px solid #f3f3f3", borderTop: "4px solid #00a3e0",
                        borderRadius: "50%", animation: "spin 0.8s linear infinite"
                    }} />
                    <p style={{ marginTop: "15px", fontSize: "13px", fontWeight: "bold", color: "#475569" }}>
                        Loading Production Data Matrix...
                    </p>
                </div>
            )}

            <Sidebar
                currentUser={currentUser}
                activeDept={activeDept}
                activeClient={activeClient}
                setActiveDept={setActiveDept}
                setActiveClient={setActiveClient}
                openMenus={openMenus}
                setOpenMenus={setOpenMenus}
                handleLogout={handleLogout}
                assignedClientNames={assignedClientNames}
                clientsList={clientsList}
                unreadCount={unreadCount}
                onNotificationClick={() => setShowNotificationPanel(true)}
            />

            <main style={{
                flex: 1,
                overflowY: ["Dashboard", "Reviewer Dashboard", "Report", "User Management", "Client Management", "Hourly Monitor"].includes(activeDept) ? "auto" : "hidden",
                overflowX: "hidden",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                height: ["Dashboard", "Reviewer Dashboard", "Report", "User Management", "Client Management", "Hourly Monitor"].includes(activeDept) ? "auto" : "100vh"
            }} className="dashboard-print-root">
                {((activeDept === "Dashboard" && isAdmin) || activeDept === "Hourly Monitor" || activeDept === "Report") && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "16px 20px",
                        padding: "16px 30px",
                        backgroundColor: "white",
                        borderBottom: "1px solid #e2e8f0"
                    }} className="top-filter-bar-print-hide">
                        {(activeDept === "Dashboard" || activeDept === "Report" || activeDept === "QC" || activeDept === "Revised") && (
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button onClick={() => setQuickRange("Today")} style={getFilterButtonStyle("Today")}>
                                    Today
                                </button>
                                <button onClick={() => setQuickRange("Month")} style={getFilterButtonStyle("Month")}>
                                    This Month
                                </button>
                            </div>
                        )}

                        {(activeDept === "Dashboard" || activeDept === "Report") && (
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <label style={{ fontSize: "11px", fontWeight: "bold", color: "#94a3b8" }}>Shift Window:</label>
                                <div style={{ display: "flex", gap: "4px", backgroundColor: "#f1f5f9", padding: "4px", borderRadius: "8px" }}>
                                    {[
                                        { value: "Live", label: "Live" },
                                        { value: "Offline", label: "Offline" },
                                        { value: "All", label: "Total" }
                                    ].map(btn => (
                                        <button
                                            key={btn.value}
                                            onClick={() => setShiftFilter(btn.value)}
                                            style={{
                                                padding: "6px 12px",
                                                fontSize: "12px",
                                                fontWeight: "800",
                                                border: "1px solid #cbd5e1",
                                                borderRadius: "6px",
                                                cursor: "pointer",
                                                backgroundColor: shiftFilter === btn.value ? "#00a3e0" : "white",
                                                color: shiftFilter === btn.value ? "white" : "#475569"
                                            }}
                                        >
                                            {btn.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(activeDept === "Dashboard" || activeDept === "Report" || activeDept === "Hourly Monitor") && (
                            <div style={{ height: "24px", width: "1px", backgroundColor: "#e2e8f0" }} />
                        )}

                        {(activeDept === "Dashboard" || activeDept === "Report" || activeDept === "Hourly Monitor") && (
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <label style={{ fontSize: "11px", fontWeight: "bold", color: "#94a3b8" }}>Client:</label>
                                <select
                                    value={selectedClient}
                                    onChange={(e) => setSelectedClient(e.target.value)}
                                    style={dateInputStyle}
                                >
                                    <option value="All">All Clients</option>
                                    {clientsList && clientsList.length > 0 ? (
                                        [
                                            ...clientsList.filter(c => {
                                                const lower = c.name.toLowerCase();
                                                return lower === "proval net" || lower === "mma";
                                            }),
                                            ...[...clientsList.filter(c => {
                                                const lower = c.name.toLowerCase();
                                                return lower !== "proval net" && lower !== "mma";
                                            })].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }))
                                        ].map(c => (
                                            <option key={c.id || c.name} value={c.name}>{c.name}</option>
                                        ))
                                    ) : (
                                        [
                                            "Proval Net", "MMA", "A1 AMC", "AAG", "AAMS", "All Pro Amc", "Appraisal Partner",
                                            "Appraisers Review", "Ascribe VL", "Core Valuation", "EA - Appraisals", "Eppraisal",
                                            "Exactus", "Financial Dimensions", "Inspectify - Hybrid", "LRES - Appraisals", "LVS",
                                            "NAN", "Property valuation network", "Signature", "Team Matthew Appraisals",
                                            "Top Notch appraisers", "Valora AMC", "Valutrust Solutions", "Zap appraisal", "AMCSSC"
                                        ].map(c => (
                                            <option key={c} value={c}>{c}</option>
                                        ))
                                    )}
                                </select>
                            </div>
                        )}

                        {/* Location Dropdown Filter */}
                        {(activeDept === "Dashboard" || activeDept === "Report" || activeDept === "Hourly Monitor") && (
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <label style={{ fontSize: "11px", fontWeight: "bold", color: "#94a3b8" }}>Location:</label>
                                <select
                                    value={selectedLocation}
                                    onChange={(e) => setSelectedLocation(e.target.value)}
                                    style={dateInputStyle}
                                >
                                    <option value="All">All Locations</option>
                                    <option value="Mumbai">Mumbai</option>
                                    <option value="Sawantwadi">Sawantwadi</option>
                                    <option value="Solapur">Solapur</option>
                                </select>
                            </div>
                        )}

                        {activeDept !== "Hourly Monitor" && (
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <label style={{ fontSize: "11px", fontWeight: "bold", color: "#94a3b8" }}>From:</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => {
                                        setStartDate(e.target.value);
                                        setDateFilterType("Custom");
                                    }}
                                    style={dateInputStyle}
                                />

                                <label style={{ fontSize: "11px", fontWeight: "bold", color: "#94a3b8" }}>To:</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => {
                                        setEndDate(e.target.value);
                                        setDateFilterType("Custom");
                                    }}
                                    style={dateInputStyle}
                                />
                            </div>
                        )}

                        {/* Right-aligned Export Action Group */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginLeft: "auto",
                            flexShrink: 0
                        }}>
                            <span style={{ fontSize: "12px", color: "#64748b", fontWeight: "600", marginRight: "4px" }}>
                                Filtered Records: <b style={{ color: "#0f172a", fontSize: "13px" }}>{activeDept === "Dashboard" && aggregatedSummary ? aggregatedSummary.metrics.total : (dashboardFiles ? dashboardFiles.length : 0)}</b>
                            </span>

                            {isManager && (
                                <div style={{ display: "inline-block" }}>
                                    <input
                                        type="file"
                                        id="bulk-import-excel-file"
                                        accept=".xlsx, .xls"
                                        onChange={handleBulkImport}
                                        style={{ display: "none" }}
                                    />
                                </div>
                            )}

                            {activeDept === "Dashboard" && (
                                <select
                                    value={exportType}
                                    onChange={(e) => setExportType(e.target.value)}
                                    style={{
                                        padding: "6px 12px",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                        color: "#475569",
                                        backgroundColor: "white",
                                        border: "1px solid #cbd5e1",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        outline: "none",
                                        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
                                    }}
                                >
                                    <option value="All">All Departments</option>
                                    <option value="QC">QC Only</option>
                                    <option value="Revised">Revised Only</option>
                                </select>
                            )}

                            {activeDept === "Dashboard" && (
                                <button
                                    onClick={handleExport}
                                    style={{
                                        padding: "6px 16px",
                                        backgroundColor: "#2f855a",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "6px",
                                        cursor: "pointer",
                                        fontSize: "12px",
                                        fontWeight: "bold",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
                                    }}
                                >
                                    Export to Excel
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Workspace Panels Switches */}
                {activeDept === "Dashboard" && (
                    isAdmin ? (
                        <StatsOverview
                            aggregatedSummary={aggregatedSummary}
                            dashboardFiles={dashboardFiles}
                            startDate={startDate}
                            endDate={endDate}
                            activeClient={selectedClient}
                            shiftFilter={shiftFilter}
                            viewMode="Summary"
                            files={files || []}
                            location={selectedLocation}
                            notifications={notifications}
                            onMarkAllRead={(isAdmin || isManager) ? null : markAllNotificationsRead}
                            onMarkSingleRead={(isAdmin || isManager) ? null : markSingleNotificationRead}
                        />
                    ) : isAccountManager ? (
                        <AccountManagerDashboard
                            currentUser={currentUser}
                            clientTatMap={clientTatMap}
                            selectedClient={selectedClient}
                            shiftFilter={shiftFilter}
                            selectedLocation={selectedLocation}
                            onOpenWorkspace={(clientName, deptName) => {
                                setActiveClient(clientName);
                                setActiveDept(deptName);
                            }}
                        />
                    ) : (
                        currentUser.name !== "Waiting..." && (
                            <ReviewerDashboard
                                currentUser={currentUser}
                                isManager={isManager}
                                clientTatMap={clientTatMap}
                                startDate={startDate}
                                endDate={endDate}
                                selectedClient={selectedClient}
                                shiftFilter={shiftFilter}
                                selectedLocation={selectedLocation}
                                onOpenWorkspace={(clientName, deptName) => {
                                    setActiveClient(clientName);
                                    setActiveDept(deptName);
                                }}
                            />
                        )
                    )
                )}

                {activeDept === "Hourly Monitor" && (
                    <StatsOverview
                        aggregatedSummary={null}
                        dashboardFiles={[]}
                        startDate={startDate}
                        endDate={endDate}
                        activeClient={selectedClient}
                        shiftFilter={shiftFilter}
                        viewMode="Hourly"
                        location={selectedLocation}
                    />
                )}

                {activeDept === "Report" && isAdmin && (
                    <DailyReports
                        dashboardFiles={dashboardFiles}
                        allFiles={files || []}
                        startDate={startDate}
                        endDate={endDate}
                        shiftFilter={shiftFilter}
                        locationFilter={selectedLocation}
                        selectedClient={selectedClient}
                        clientsList={clientsList}
                    />
                )}

                {activeDept === "User Management" && isAdmin && (
                    <div style={{ padding: "30px" }}>
                        <UserManagement currentUser={currentUser} onUserUpdate={fetchUserLocations} />
                    </div>
                )}

                {activeDept === "Client Management" && isAdmin && (
                    <div style={{ padding: "30px" }}>
                        <ClientManagement currentUser={currentUser} onClientUpdate={fetchClients} />
                    </div>
                )}

                {/* EXCLUDED: Prevent view mode from collapsing into standard grid list data tables */}
                {activeDept !== "Dashboard" && activeDept !== "Reviewer Dashboard" && activeDept !== "Hourly Monitor" && activeDept !== "Report" && activeDept !== "User Management" && activeDept !== "Client Management" && (
                    <FileTable
                        files={files || []}
                        setFiles={setFiles}
                        activeDept={activeDept}
                        activeClient={activeClient}
                        currentUser={currentUser}
                        isManager={isManager}
                        isAdmin={isAdmin}
                        setShowManualModal={setShowManualModal}
                        fetchData={fetchData}
                        clientTatMap={clientTatMap}
                    />
                )}
            </main>

            <ManualEntryModal
                show={showManualModal}
                setShow={setShowManualModal}
                formData={formData}
                handleManualInputChange={handleManualInputChange}
                submitManualEntry={submitManualEntry}
                isManager={isManager}
            />

            {/* Toast Notification */}
            {activeToast && (
                <div style={{
                    position: "fixed",
                    bottom: "20px",
                    right: "20px",
                    backgroundColor: activeToast.type === "error" ? "#fee2e2" : (activeToast.type === "success" ? "#ecfdf5" : "#1e293b"),
                    color: activeToast.type === "error" ? "#991b1b" : (activeToast.type === "success" ? "#065f46" : "white"),
                    padding: "16px 20px",
                    borderRadius: "10px",
                    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.2), 0 4px 6px -4px rgba(0, 0, 0, 0.2)",
                    zIndex: 99999,
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    maxWidth: "350px",
                    borderLeft: activeToast.type === "error" ? "4px solid #ef4444" : (activeToast.type === "success" ? "4px solid #10b981" : "4px solid #00a3e0"),
                    animation: "slideIn 0.3s ease-out"
                }}>
                    <div style={{ fontSize: "20px" }}>
                        {activeToast.type === "error" ? "❌" : (activeToast.type === "success" ? "✅" : "🔔")}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: "bold", fontSize: "13px", color: activeToast.type === "error" ? "#ef4444" : (activeToast.type === "success" ? "#10b981" : "#00a3e0") }}>
                            {activeToast.title || "File Assigned!"}
                        </div>
                        <div style={{ fontSize: "12px", marginTop: "2px", color: activeToast.type === "error" ? "#991b1b" : (activeToast.type === "success" ? "#065f46" : "#cbd5e1") }}>
                            {activeToast.message}
                        </div>
                    </div>
                    <button
                        onClick={() => setActiveToast(null)}
                        style={{
                            background: "transparent",
                            border: "none",
                            color: "#94a3b8",
                            cursor: "pointer",
                            fontSize: "14px",
                            padding: "0 4px"
                        }}
                    >
                        ✕
                    </button>
                </div>
            )}

            {/* Notification List Modal */}
            {showNotificationPanel && (
                <div style={{
                    position: "fixed",
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.3)",
                    backdropFilter: "blur(4px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 99999
                }}>
                    <div style={{
                        backgroundColor: "white",
                        borderRadius: "16px",
                        width: "500px",
                        maxWidth: "90%",
                        maxHeight: "80vh",
                        display: "flex",
                        flexDirection: "column",
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                        overflow: "hidden"
                    }}>
                        <div style={{
                            padding: "16px 20px",
                            borderBottom: "1px solid #f1f5f9",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#f8fafc"
                        }}>
                            <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "bold", color: "#0f172a", display: "flex", alignItems: "center", gap: "8px" }}>
                                🔔 Notifications Feed
                            </h3>
                            {!(isAdmin || isManager) && (
                                <button
                                    onClick={markAllNotificationsRead}
                                    style={{
                                        fontSize: "12px",
                                        color: "#00a3e0",
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                        fontWeight: "800"
                                    }}
                                >
                                    Mark all as read
                                </button>
                            )}
                        </div>

                        <div style={{ flex: 1, overflowY: "auto", padding: "10px 20px" }}>
                            {notifications.length === 0 ? (
                                <div style={{ textAlign: "center", padding: "40px 20px", color: "#64748b" }}>
                                    <div style={{ fontSize: "24px", marginBottom: "8px" }}>📭</div>
                                    <div style={{ fontSize: "13px" }}>No notifications yet.</div>
                                </div>
                            ) : (
                                notifications.map(n => {
                                    const showAsUnread = !(isAdmin || isManager) && !n.is_read;
                                    return (
                                        <div
                                            key={n.id}
                                            onClick={() => showAsUnread && markSingleNotificationRead(n.id)}
                                            style={{
                                                padding: "12px",
                                                borderBottom: "1px solid #f1f5f9",
                                                borderRadius: "8px",
                                                marginBottom: "8px",
                                                cursor: showAsUnread ? "pointer" : "default",
                                                backgroundColor: showAsUnread ? "#f0f9ff" : "transparent",
                                                borderLeft: showAsUnread ? "3px solid #00a3e0" : "3px solid transparent",
                                                transition: "all 0.2s"
                                            }}
                                        >
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                                <div style={{ fontSize: "13px", fontWeight: showAsUnread ? "800" : "500", color: "#1e293b" }}>
                                                    {n.message}
                                                </div>
                                                {showAsUnread && (
                                                    <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: "#00a3e0" }} />
                                                )}
                                            </div>
                                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
                                                <span>Assigned by: {n.sender_name}</span>
                                                <span>{new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div style={{
                            padding: "12px 20px",
                            borderTop: "1px solid #f1f5f9",
                            display: "flex",
                            justifyContent: "flex-end",
                            backgroundColor: "#f8fafc"
                        }}>
                            <button
                                onClick={() => setShowNotificationPanel(false)}
                                style={{
                                    padding: "8px 16px",
                                    backgroundColor: "#0f172a",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    fontWeight: "600"
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                    @keyframes slideIn {
                        from { transform: translateY(100px); opacity: 0; }
                        to { transform: translateY(0); opacity: 1; }
                    }
                `}
            </style>
        </div>
    );
};

export default AdminDashboard;
